/**
 * usePlayerMachine — React adapter for the pure `PlayerMachine`
 * reducer. Owns all the scheduling effects:
 *
 *   - cinematic intro beat timer
 *   - auto-advance timer per slide
 *   - keyboard shortcuts (Esc / Arrows / Space / P)
 *   - curtain completion timer
 *
 * Exposes a small intent API (next / prev / toggle / restart / begin /
 * openRespond / closeRespond) plus read-only derived values
 * (currentSlide, isFinalSlide).
 */

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  createInitialState,
  reducePlayer,
  type PlayerEvent,
  type PlayerPhase,
  type PlayerState,
  type ProposalType,
} from './PlayerMachine';
import type { PreviewSlideData } from '../PreviewSlide';

interface UsePlayerMachineArgs {
  open: boolean;
  slides: PreviewSlideData[];
  openingAnimation: string;
  proposalType?: ProposalType;
  onClose: () => void;
}

export interface PlayerMachineApi {
  phase: PlayerPhase;
  index: number;
  direction: 1 | -1;
  prevSlide: PreviewSlideData | null;
  playbackKey: number;
  introBeat: number;
  curtainPending: boolean;
  isPlaying: boolean;
  isComplete: boolean;
  currentSlide: PreviewSlideData | undefined;
  isFinalSlide: boolean;
  showRespondDialog: boolean;
  showIntro: boolean;

  begin(): void;
  next(): void;
  prev(): void;
  togglePlay(): void;
  restart(): void;
  openRespond(): void;
  closeRespond(): void;
}

const INTRO_BEAT_MS = [400, 800, 600, 600] as const;

export function usePlayerMachine({
  open,
  slides,
  openingAnimation,
  proposalType,
  onClose,
}: UsePlayerMachineArgs): PlayerMachineApi {
  const [state, dispatch] = useReducer(
    (s: PlayerState, e: PlayerEvent) =>
      reducePlayer(s, e, { slides, openingAnimation }),
    createInitialState(),
  );

  /* ─── reset / open lifecycle ───────────────────────────────────── */
  useEffect(() => {
    if (open) dispatch({ type: 'OPEN', slides });
    else dispatch({ type: 'CLOSE' });
    // We don't want content-change to push us back to the closed state
    // — only the `open` boolean drives OPEN/CLOSE. eslint disables
    // the warning intentionally.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Content edits while open should bump playbackKey so the stage
  // remounts with fresh data, but NOT reset the playhead. The reducer
  // implements this via CONTENT_CHANGED.
  const contentSig = useMemo(
    () =>
      JSON.stringify(
        (slides ?? []).map((s) => ({
          type: s.type,
          contentText: s.contentText ?? '',
          imageUrl: s.imageUrl ?? '',
          effectEntry: s.effectEntry ?? '',
          effectAmbient: s.effectAmbient ?? '',
          effectIntensity: s.effectIntensity ?? 0,
          duration: s.duration ?? 0,
          enableAutoAdvance: s.enableAutoAdvance ?? true,
          pageTransitionId: s.pageTransitionId ?? null,
        })),
      ),
    [slides],
  );
  useEffect(() => {
    if (!open) return;
    dispatch({ type: 'CONTENT_CHANGED' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentSig]);

  /* ─── intro beat timer ─────────────────────────────────────────── */
  const introTimersRef = useRef<number[]>([]);
  useEffect(() => {
    introTimersRef.current.forEach((id) => window.clearTimeout(id));
    introTimersRef.current = [];
    if (!open || state.phase !== 'intro') return;
    let acc = 0;
    for (let beat = 1; beat <= INTRO_BEAT_MS.length; beat += 1) {
      acc += INTRO_BEAT_MS[beat - 1] ?? 0;
      const t = window.setTimeout(() => {
        dispatch({ type: 'INTRO_TICK', beat });
      }, acc);
      introTimersRef.current.push(t);
    }
    return () => {
      introTimersRef.current.forEach((id) => window.clearTimeout(id));
      introTimersRef.current = [];
    };
  }, [open, state.phase]);

  /* ─── curtain completion timer ─────────────────────────────────── */
  const curtainTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (curtainTimerRef.current) {
      window.clearTimeout(curtainTimerRef.current);
      curtainTimerRef.current = null;
    }
    if (!open) return;
    if (openingAnimation !== 'cinematic-curtain') {
      dispatch({ type: 'CURTAIN_DONE' });
      return;
    }
    curtainTimerRef.current = window.setTimeout(() => {
      dispatch({ type: 'CURTAIN_DONE' });
    }, 5300);
    return () => {
      if (curtainTimerRef.current) {
        window.clearTimeout(curtainTimerRef.current);
        curtainTimerRef.current = null;
      }
    };
  }, [open, openingAnimation]);

  /* ─── auto-begin ───────────────────────────────────────────────── */
  // Once the curtain has finished and we're still in `intro`, advance
  // the player into `playing` so slide 0 actually appears. The intro
  // overlay still gets to play its beat sequence, but only on templates
  // with `openingAnimation !== 'cinematic-curtain'` (where there's no
  // curtain timer to gate on). For curtain openings the intro shows up
  // immediately after the curtain panels part, then auto-advances.
  useEffect(() => {
    if (!open) return;
    if (state.phase !== 'intro') return;
    if (state.curtainPending) return;
    const t = window.setTimeout(() => dispatch({ type: 'BEGIN' }), 1200);
    return () => window.clearTimeout(t);
  }, [open, state.phase, state.curtainPending]);

  /* ─── auto-advance timer ───────────────────────────────────────── */
  const advanceTimerRef = useRef<number | null>(null);
  useEffect(() => {
    if (advanceTimerRef.current) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
    if (!open || state.phase !== 'playing' || state.index < 1) return;
    const slideIdx = state.index - 1;
    const slide = slides[slideIdx];
    if (!slide) return;
    if (slide.enableAutoAdvance === false) return;
    // Auto-advance is driven by the slide's `duration` field. Treat
    // 0 / missing / NaN as a "play forever" slide (no auto-advance) so
    // the editor's blank-template 5s default stays sane but a
    // misconfigured slide doesn't strand the deck mid-way.
    const rawDuration = Number(slide.duration);
    const duration = Number.isFinite(rawDuration) && rawDuration > 0 ? rawDuration : 0;
    if (duration <= 0) return;
    const ms = Math.max(1000, Math.round(duration * 1000));
    advanceTimerRef.current = window.setTimeout(() => {
      // DATE templates get the respond dialog at the end of the deck,
      // not the generic finale.
      const isLast = slideIdx >= slides.length - 1;
      if (isLast && proposalType === 'DATE') {
        dispatch({ type: 'OPEN_RESPOND' });
        return;
      }
      dispatch({ type: 'AUTO_ADVANCE_TICK' });
    }, ms);
    return () => {
      if (advanceTimerRef.current) {
        window.clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [open, state.phase, state.index, slides, proposalType]);

  /* ─── derived values ────────────────────────────────────────────── */
  const currentSlide = slides[state.index - 1];
  const isLastSlide = slides.length > 0 && state.index - 1 >= slides.length - 1;
  const isFinalSlide =
    currentSlide?.type === 'FINAL' ||
    (isLastSlide && proposalType === 'DATE');

  /* ─── intent API ───────────────────────────────────────────────── */
  const begin = useCallback(() => dispatch({ type: 'BEGIN' }), []);
  const togglePlay = useCallback(() => dispatch({ type: 'TOGGLE_PLAY' }), []);

  const next = useCallback(() => {
    // Wrap NEXT so DATE finale stays consistent with auto-advance.
    if (slides.length === 0) {
      dispatch({ type: 'NEXT' });
      return;
    }
    const isLast = state.index - 1 >= slides.length - 1;
    if (isLast && proposalType === 'DATE') {
      dispatch({ type: 'OPEN_RESPOND' });
      return;
    }
    dispatch({ type: 'NEXT' });
  }, [slides.length, state.index, proposalType]);

  const prev = useCallback(() => {
    dispatch({ type: 'PREV' });
    // PREV closes any open respond dialog (mirrors legacy behaviour).
    if (state.phase === 'respond') dispatch({ type: 'CLOSE_RESPOND' });
  }, [state.phase]);

  const restart = useCallback(() => dispatch({ type: 'RESTART' }), []);
  const openRespond = useCallback(() => dispatch({ type: 'OPEN_RESPOND' }), []);
  const closeRespond = useCallback(() => dispatch({ type: 'CLOSE_RESPOND' }), []);

  /* ─── keyboard shortcuts ───────────────────────────────────────── */
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      const inIntro = state.phase === 'intro';
      if (e.key === 'ArrowRight' || e.key === ' ') {
        if (!inIntro) {
          e.preventDefault();
          next();
        }
      } else if (e.key === 'ArrowLeft') {
        if (!inIntro) {
          e.preventDefault();
          prev();
        }
      } else if (e.key.toLowerCase() === 'p') {
        if (!inIntro) dispatch({ type: 'TOGGLE_PLAY' });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, state.phase, slides.length, onClose]);

  return {
    phase: state.phase,
    index: state.index,
    direction: state.direction,
    prevSlide: state.prevSlide,
    playbackKey: state.playbackKey,
    introBeat: state.introBeat,
    curtainPending: state.curtainPending,
    isPlaying: state.phase === 'playing',
    isComplete: state.phase === 'done',
    currentSlide,
    isFinalSlide,
    showRespondDialog: state.phase === 'respond',
    showIntro: state.phase === 'intro',
    begin,
    next,
    prev,
    togglePlay,
    restart,
    openRespond,
    closeRespond,
  };
}
